import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getPerformance } from 'firebase/performance';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const requiredFirebaseKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const missingFirebaseKeys = requiredFirebaseKeys.filter((key) => !firebaseConfig[key]);

let app = null;
let auth = null;
let db = null;
let storage = null;
let performance = null;
let analytics = null;
let firebaseReady = false;
let firebaseConfigError = null;

if (missingFirebaseKeys.length > 0) {
  firebaseConfigError = `Missing Firebase env vars: ${missingFirebaseKeys.join(', ')}`;
  console.error(`❌ Firebase configuration incomplete. ${firebaseConfigError}`);
  console.error('ℹ️ Fill frontend/.env with VITE_FIREBASE_* values from Firebase Web App settings.');
} else {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    firebaseReady = true;

    // Keep session persistence behavior unchanged.
    setPersistence(auth, browserSessionPersistence).catch((error) => {
      console.warn('⚠️ Failed to set Firebase session persistence:', error);
    });

    try {
      performance = getPerformance(app);
    } catch (error) {
      console.warn('⚠️ Firebase Performance monitoring unavailable:', error?.message || error);
    }

    try {
      if (import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
        analytics = getAnalytics(app);
      }
    } catch (error) {
      console.warn('⚠️ Firebase Analytics unavailable:', error?.message || error);
    }
  } catch (error) {
    firebaseConfigError = error?.message || 'Firebase initialization failed';
    firebaseReady = false;
    console.error('❌ Firebase initialization failed:', error);
  }
}

export { app, auth, db, storage, performance, analytics, firebaseReady, firebaseConfigError };

export default app;
